/**
 * useSandbox — Hook para gerenciar sessões de sandbox (Modo Agente)
 *
 * API simplificada:
 *   - startSandbox() / stopSandbox()
 *   - status: "idle" | "starting" | "running" | "stopping" | "error"
 *   - vncUrl, wsUrl, sandboxUrl
 *   - activeView / setActiveView
 *
 * @module hooks/useSandbox
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { trpc } from "../lib/trpc.js";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
export type SandboxStatus = "idle" | "starting" | "running" | "stopping" | "error";
export type SandboxView = "vnc" | "terminal" | "split";

export interface SandboxSession {
  id: string;
  status: string;
  sandboxUrl: string | null;
  vncUrl: string | null;
  wsUrl: string | null;
  createdAt: number;
  expiresAt: number | null;
}

export interface SandboxEvent {
  id: string;
  eventType: string;
  payload: Record<string, unknown>;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useSandbox(conversationId?: string) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<SandboxStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [vncUrl, setVncUrl] = useState<string | null>(null);
  const [wsUrl, setWsUrl] = useState<string | null>(null);
  const [sandboxUrl, setSandboxUrl] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<SandboxView>("split");
  const wsRef = useRef<WebSocket | null>(null);

  // tRPC mutations
  const createMutation = trpc.sandbox.create.useMutation();
  const stopMutation = trpc.sandbox.stop.useMutation();

  /**
   * Inicia um novo sandbox.
   */
  const startSandbox = useCallback(
    async (brainTaskId?: string) => {
      if (status === "starting" || status === "running") return;

      setStatus("starting");
      setError(null);

      try {
        const result = await createMutation.mutateAsync({
          conversationId,
          brainTaskId,
        });

        const session = result as unknown as SandboxSession;
        setSessionId(session.id);
        setVncUrl(session.vncUrl);
        setWsUrl(session.wsUrl);
        setSandboxUrl(session.sandboxUrl);
        setStatus("running");

        console.log(`[useSandbox] Sandbox iniciado: ${session.id}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erro ao criar sandbox";
        setError(msg);
        setStatus("error");
        console.error(`[useSandbox] Erro: ${msg}`);
      }
    },
    [status, conversationId, createMutation],
  );

  /**
   * Encerra o sandbox ativo.
   */
  const stopSandbox = useCallback(
    async (reason: string = "user_stopped") => {
      if (!sessionId) return;

      setStatus("stopping");
      try {
        await stopMutation.mutateAsync({
          sessionId,
          reason,
        });

        // Limpar estado
        setSessionId(null);
        setVncUrl(null);
        setWsUrl(null);
        setSandboxUrl(null);
        setStatus("idle");
        setError(null);

        // Fechar WebSocket
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }

        console.log(`[useSandbox] Sandbox encerrado: ${sessionId}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erro ao encerrar sandbox";
        setError(msg);
        setStatus("error");
      }
    },
    [sessionId, stopMutation],
  );

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return {
    // Estado
    sessionId,
    status,
    error,
    activeView,
    setActiveView,

    // URLs
    vncUrl,
    wsUrl,
    sandboxUrl,

    // Ações
    startSandbox,
    stopSandbox,
  };
}
